import { usePostQcLedgerApi, usePutQcLedgerApi } from 'api/qc_ledger'
import { Flex } from 'components/shared/flex'
import { DateField, InputField } from 'components/shared/input'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useContext, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'
import { ApiError } from 'utils/network/api_hooks'
import { QcLedger, QcLedgerForm } from 'entities/qc_ledger'
import { useFetchOrganizationsApi } from 'api/organization'

export type FormViewProps = {
    form: Form<QcLedgerForm>
    apiError: ApiError
}

const FormView = ({ form, apiError }: FormViewProps) => {
    const organizationsApi = useFetchOrganizationsApi()
    useEffect(() => {
        organizationsApi.execute()
    }, [])

    return (
        <>
            <Flex>
                <DateField form={form} attr="reportingDate" apiError={apiError} label="指導監督医への報告日" required />
            </Flex>
            <Flex>
                <DateField form={form} attr="start" apiError={apiError} label="管理限界を外れた日(開始)" required />
                <DateField form={form} attr="end" apiError={apiError} label="管理限界を外れた日(終了)" required />
            </Flex>

            <InputField form={form} attr="cause" apiError={apiError} label="原因" required as="textarea" />
            <InputField form={form} attr="action" apiError={apiError} label="対応措置" required as="textarea" />
            <InputField form={form} attr="measures" apiError={apiError} label="防止策" required as="textarea" />
        </>
    )
}

type NewQcLedgerFormProps = {
    onComplete?: () => void
}

export const NewQcLedgerForm = (props: NewQcLedgerFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePostQcLedgerApi()
    const form = useForm<QcLedgerForm>({})
    const [show, setShow] = useState<boolean>(false)

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                setShow(false)
                props.onComplete()
            }
        }
    }, [api.loading])

    const handleClose = () => {
        setShow(false)
    }

    return (
        <>
            <Button
                variant="success"
                onClick={() => {
                    setShow(true)
                }}
            >
                新規登録
            </Button>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>統計学的精度管理台帳の登録</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <>
                        <FormView form={form} apiError={api.apiError} />
                    </>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        閉じる
                    </Button>
                    <Button variant="success" onClick={handleUpdate}>
                        登録
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}
type EditQcLedgerFormProps = {
    qcLedger: QcLedger
    show: boolean
    onClose: () => void
    onComplete?: () => void
}

export const EditQcLedgerForm = (props: EditQcLedgerFormProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const api = usePutQcLedgerApi()
    const form = useForm<QcLedgerForm>(props.qcLedger)

    useEffectSkipFirst(() => {
        form.set(props.qcLedger)
    }, [props.qcLedger])

    const handleUpdate = () => {
        api.execute(form)
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
        if (api.isSuccess()) {
            if (props.onComplete) {
                props.onComplete()
            }
        }
    }, [api.loading])

    return (
        <Modal show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>統計学的精度管理台帳の編集</Modal.Title>
            </Modal.Header>
            <Modal.Body>{form.object.id && <FormView form={form} apiError={api.apiError} />}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={props.onClose}>
                    閉じる
                </Button>
                <Button variant="success" onClick={handleUpdate}>
                    更新
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
